import { vidTranslation } from '@/api';
import Uppy from '@uppy/core';
import XHR from '@uppy/xhr-upload';
import { TinyEmitter } from 'tiny-emitter';

const FILE_SLICE_SIZE = 5 * 1024 * 1024;

function sliceFile(file) {
  const chunks = [];
  let pointer = 0;
  while (pointer < file.size) {
    chunks.push({
      eTag: '',
      blob: file.slice(pointer, (pointer += FILE_SLICE_SIZE)),
    });
  }
  return chunks;
}
export default class Uploader extends TinyEmitter {
  #file;
  chunks = [];
  #successCount = 0;
  #uploadCount = 0;
  #uploadId;
  #uppy;

  constructor() {
    super();
    this.#uppy = new Uppy().use(XHR, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      formData: false,
      getResponseData: (responseText, response) => {
        if (!response) {
          return {
            status: 0,
            body: responseText,
            url: null,
          };
        }
        return {
          url: response.responseURL,
          status: response.status,
          body: responseText,
        };
      },
      onBeforeRequest: (xhr, retryCount, files) => {
        xhr.index = files[0].meta.index;
        this.#uppy.getPlugin('XHRUpload').setOptions({
          endpoint: files[0].meta.url,
        });
      },
      onAfterResponse: (xhr) => {
        const eTag = xhr.getResponseHeader('ETag');
        if (eTag && this.chunks[xhr.index]) {
          this.chunks[xhr.index].eTag = eTag;
        }
      },
    });
    this.#bindEvents();
  }

  #bindEvents() {
    this.#uppy.on('complete', ({ successful }) => {
      this.#uploadCount++;
      this.#successCount += successful.length;
      this.emit('progress', Math.max(0.8, this.#successCount / this.chunks.length));

      if (this.#successCount === this.chunks.length) {
        this.emit('success', this.generateSuccessResult());
      } else if (this.#uploadCount === this.chunks.length) {
        this.emit('failed');
      }
    });
  }

  generateSuccessResult() {
    return {
      uploadId: this.#uploadId,
      completedParts: this.chunks.map((chunk) => ({
        partNumber: chunk.partNumber,
        eTag: chunk.eTag,
      })),
    };
  }

  uploadFile(file) {
    this.#file = file;
    this.chunks = [
      {
        eTag: '',
        blob: file,
      },
    ];
    this.#createUpload();
  }

  uploadFileWithSlice(file) {
    this.#file = file;
    this.chunks = sliceFile(file);
    this.#createUpload();
  }

  uploadChunks(urls) {
    urls.forEach((url, index) => {
      this.chunks[index].partNumber = index + 1;
      if (index === 0) {
        this.#uppy.getPlugin('XHRUpload').setOptions({
          endpoint: url,
        });
      }

      this.#uppy.addFile({
        name: `chunk-${index}`,
        data: this.chunks[index].blob,
        meta: {
          index,
          url: urls[index + 1],
        },
      });

      this.#uppy.upload();
    });
  }

  #createUpload() {
    let progress = 0;
    const interval = setInterval(() => {
      if (progress < 0.8) {
        progress += 0.02;
        this.emit('progress', progress);
      } else {
        progress = 0.8;
        this.emit('progress', progress);
        clearInterval(interval);
      }
    }, 100);
    vidTranslation
      .newUploadTask({
        ext: this.#file.name,
        assetSize: this.#file.size,
        parts: this.chunks.length,
      })
      .then((res) => {
        if (res?.code !== 0) {
          console.error('create new upload task failed');
          throw new Error('create new upload task failed');
        } else {
          const { uploadId, urls } = res.data;
          this.#uploadId = uploadId;
          progress = 0.8;
          this.emit('progress', progress);
          this.emit('getShardlink');
          clearInterval(interval);
          this.uploadChunks(urls);
        }
      })
      .catch((err) => {
        this.emit('error', err);
      });
  }

  retry() {
    this.#uppy.retryAll().then(({ failed }) => {
      if (failed.length > 0) {
        this.emit('retry-failed');
      }
    });
  }

  stop() {
    this.#uppy.cancelAll();
  }
}
